/**
 * Safe checkout redirect with URL validation and double-click protection.
 *
 * Fixes:
 * - Double-click guard prevents duplicate Stripe sessions
 * - URL validation ensures we only redirect to Stripe checkout
 * - Microtask delay lets React commit pending state updates before navigation
 */

let checkoutInFlight = false;

export function isCheckoutInFlight(): boolean {
  return checkoutInFlight;
}

export async function safeCheckoutRedirect(
  getUrl: () => Promise<{ url: string; requestId?: string }>,
  opts?: {
    onError?: (err: Error) => void;
    fallbackUrl?: string;
  },
): Promise<void> {
  // Double-click guard
  if (checkoutInFlight) return;
  checkoutInFlight = true;

  try {
    const { url } = await getUrl();

    // Validate URL exists
    if (!url || typeof url !== 'string') {
      throw new Error('Checkout session returned empty URL');
    }

    // Validate URL is a Stripe checkout URL
    if (!url.startsWith('https://checkout.stripe.com/')) {
      throw new Error('Invalid checkout URL received');
    }

    // Microtask delay to let React commit any pending state updates
    await new Promise((resolve) => setTimeout(resolve, 0));

    window.location.href = url;
  } catch (err) {
    checkoutInFlight = false;
    if (opts?.onError) {
      opts.onError(err instanceof Error ? err : new Error(String(err)));
    } else if (opts?.fallbackUrl) {
      window.location.href = opts.fallbackUrl;
    }
  }
}

/**
 * Reset the in-flight guard. Call on page load or component re-mount
 * after a failed checkout (e.g. user pressed back).
 */
export function resetCheckoutGuard(): void {
  checkoutInFlight = false;
}
